import React from 'react';
import { Star, Warning } from '@phosphor-icons/react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BugPriority } from 'shared/types/bug-report';

interface PrioritySelectorProps {
  priority: BugPriority;
  onChange: (priority: BugPriority) => void;
  className?: string;
}

export const PrioritySelector: React.FC<PrioritySelectorProps> = ({
  priority,
  onChange,
  className = ''
}) => {
  const getPriorityConfig = () => {
    switch (priority) {
      case BugPriority.LOW:
        return { stars: 1, color: 'text-gray-400', label: 'Lage prioriteit', isCritical: false };
      case BugPriority.MEDIUM:
        return { stars: 2, color: 'text-yellow-500', label: 'Normale prioriteit', isCritical: false };
      case BugPriority.HIGH:
        return { stars: 3, color: 'text-orange-500', label: 'Hoge prioriteit', isCritical: false };
      case BugPriority.CRITICAL:
        return { stars: 0, color: 'text-red-600', label: 'Kritieke prioriteit', isCritical: true };
      default:
        return { stars: 2, color: 'text-yellow-500', label: 'Normale prioriteit', isCritical: false };
    }
  };

  const config = getPriorityConfig();
  const totalStars = 3;

  const handleStarClick = (starIndex: number) => {
    // starIndex: 0, 1, 2 for stars 1, 2, 3
    const newStarCount = starIndex + 1;

    if (newStarCount === 1) onChange(BugPriority.LOW);
    else if (newStarCount === 2) onChange(BugPriority.MEDIUM);
    else onChange(BugPriority.HIGH);
  };

  const handleCriticalClick = () => {
    onChange(BugPriority.CRITICAL);
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Stars */}
      <div className="flex items-center gap-0.5">
        {[...Array(totalStars)].map((_, index) => {
          const isActive = !config.isCritical && index < config.stars;
          return (
            <TooltipProvider key={index}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => handleStarClick(index)}
                    className={`transition-all hover:scale-110 focus:outline-none ${
                      isActive ? config.color : 'text-gray-300'
                    }`}
                  >
                    <Star
                      size={20}
                      weight={isActive ? 'fill' : 'regular'}
                      className="transition-all"
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {index === 0 && 'Laag'}
                    {index === 1 && 'Normaal'}
                    {index === 2 && 'Hoog'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>

      {/* Critical (Exclamation mark) */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleCriticalClick}
              className={`transition-all hover:scale-110 focus:outline-none ml-1 ${
                config.isCritical ? 'text-red-600' : 'text-gray-300'
              }`}
            >
              <Warning
                size={20}
                weight={config.isCritical ? 'fill' : 'regular'}
                className="transition-all"
              />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Kritiek</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Label */}
      <span className={`text-sm font-medium ${config.color} ml-1`}>
        {config.label}
      </span>
    </div>
  );
};
